import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { SEC_AUTH_M_TK } from "constants/AuthConstant";
import { postService } from "services/APIService";
import API from "services/API";
import FirebaseService from "services/FirebaseService";
import { v4 as uuidv4 } from "uuid";
import { triggerSubscriptionUpdate } from "utils/subscriptionUtils";
import { triggerNavigationUpdateAfterAuth } from "utils/navigationUpdateUtils";
import { performFullLogout } from "utils/logoutUtils";
import {
  buildOAuthRequestBody,
  parseGoogleAuthError,
  persistGoogleAuthSession,
  parseOAuthErrorPayload,
  isOAuthSuccessResponse,
} from "utils/googleAuthUtils";
import { parseMicrosoftAuthError } from "utils/microsoftAuthUtils";


export const initialState = {
  loading: false,
  message: "",
  showMessage: false,
  redirect: "",
  token: localStorage.getItem(SEC_AUTH_M_TK) || null,
  tokenExpiration: null,
  passwordResetSuccess: false,
  termsAndConditions: null,
  termsLoading: false,
  termsError: null,

  loginEmail: "",
  isOtpRequired: false,
  otpLoading: false,
  isNewUser: false,
};

const isOAuthSuccessHttpStatus = (status) => status >= 200 && status < 300;

const runOAuthIdTokenAuth = async (
  endpoint,
  idToken,
  providerName,
  parseError,
  { rejectWithValue },
  { mode = 'login', signupFields = {} } = {}
) => {
  if (!idToken) {
    return rejectWithValue({
      message: `${providerName} sign-in was cancelled or failed.`,
      errorCode: "",
      status: 0,
    });
  }

  try {
    const { clearAllLocalStorage, clearAllSessionStorage, clearReduxState } = await import("../../utils/logoutUtils");

    clearAllLocalStorage();
    clearAllSessionStorage();
    clearReduxState();

    const response = await postService(
      endpoint,
      buildOAuthRequestBody(idToken, mode, signupFields)
    );

    if (isOAuthSuccessHttpStatus(response.status) && isOAuthSuccessResponse(response.data)) {
      const session = persistGoogleAuthSession(response.data);
      if (!session?.token) {
        return rejectWithValue({
          message: `${providerName} authentication failed`,
          errorCode: "",
          status: response.status,
        });
      }

      triggerSubscriptionUpdate();
      triggerNavigationUpdateAfterAuth();
      return session;
    }

    const { message, errorCode, status, rawResponse } = parseOAuthErrorPayload(
      response.data,
      response.status,
      ''
    );
    return rejectWithValue({
      message: message || `${providerName} authentication failed`,
      errorCode,
      status,
      rawResponse,
    });
  } catch (err) {
    const { status, errorCode, message, rawResponse } = parseError(err);
    return rejectWithValue({ message, errorCode, status, rawResponse });
  }
};

const runGoogleAuth = async (endpoint, { idToken, mode, signupFields }, thunkAPI) =>
  runOAuthIdTokenAuth(endpoint, idToken, "Google", parseGoogleAuthError, thunkAPI, { mode, signupFields });

export const signIn = createAsyncThunk(
  "auth/signIn",
  async (data, { rejectWithValue, dispatch }) => {
    console.log("🔑 Login data received:", data);
    const { email, password } = data;
    try {
      // Import logout utilities dynamically to avoid circular dependencies
      const { clearAllLocalStorage, clearAllSessionStorage, clearReduxState } = await import("../../utils/logoutUtils");

      // Preventive cleanup: Clear any residual data from previous sessions
      clearAllLocalStorage();
      clearAllSessionStorage();
      clearReduxState();

      const response = await postService("/login", {
        username: email,
        password: password,
      });
      console.log("🚀 Login response:", response);
      console.log("📧 Response data:", response.data);
      if (response.status === 200) {
        if(response.data?.message?.includes("OTP has been sent")) {
          return {
            type: 'otp_required',
            email: response.data.email,
            message: response.data.message
          };
        }
        
        // Legacy flow: direct token response
        const token = response.data.token;
        
        // Set new user data
        localStorage.setItem(SEC_AUTH_M_TK, token);
        localStorage.setItem("design", response.data.role);
        localStorage.setItem("_un", email);
        localStorage.setItem("convKey", `UI_${email}_${uuidv4()}`);

        // Trigger subscription update to ensure the subscription status is refreshed
        triggerSubscriptionUpdate();
        
        // Trigger navigation update to ensure menu reflects new subscription status
        triggerNavigationUpdateAfterAuth();

        return {
          type: 'direct_login',
          token: token
        };
      } else {
        return rejectWithValue(response.message);
      }
    } catch (err) {
      const backendMessage =
      err?.response?.data?.detail?.message ||
      err?.response?.data?.message ||
      err?.response?.data?.detail ||
      err.message ||
      "Error";
  
    return rejectWithValue(backendMessage);
    }
  }
);



// New action for login OTP verification
export const verifyLoginOtp = createAsyncThunk(
  "auth/verifyLoginOtp",
  async (data, { rejectWithValue, dispatch }) => {
    const { email, otp } = data;
    try {
      const response = await postService("/verify-otp", {
        email: email,
        otp: otp
      });

      if (response.status === 200) {
        const token = response.data.token;
        const role = response.data.role;

        // Set new user data
        localStorage.setItem(SEC_AUTH_M_TK, token);
        localStorage.setItem("design", role);
        localStorage.setItem("_un", email);
        localStorage.setItem("convKey", `UI_${email}_${uuidv4()}`);

        // Trigger subscription update to ensure the subscription status is refreshed
        triggerSubscriptionUpdate();
        
        // Trigger navigation update to ensure menu reflects new subscription status
        triggerNavigationUpdateAfterAuth();

        return token;
      } else {
        return rejectWithValue(
          response.data?.detail?.message ||
          response.data?.message ||
          response.data?.detail ||
          "OTP verification failed"
        );
      }
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.detail?.message ||
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err.message ||
        "Error verifying OTP"
      );
    }
  }
);


export const sendEmailOtp = createAsyncThunk(
  "auth/sendEmailOtp",
  async (data, { rejectWithValue }) => {
    const { email } = data;
    try {
      const response = await postService("/send-email-otp", { email });

      if (response.status >= 200 && response.status < 300) {
        return response.data;
      }
      return rejectWithValue(
        response.data?.detail?.message ||
          response.data?.message ||
          response.data?.detail ||
          "Failed to send email OTP"
      );
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.detail?.message ||
          err?.response?.data?.message ||
          err?.response?.data?.detail ||
          err.message ||
          "Failed to send email OTP"
      );
    }
  }
);

export const verifyEmailOtp = createAsyncThunk(
  "auth/verifyEmailOtp",
  async (data, { rejectWithValue }) => {
    const { email, otp } = data;
    try {
      const response = await postService("/verify-email-otp", { email, otp });

      if (response.status === 200) {
        return response.data;
      }
      return rejectWithValue(
        response.data?.detail?.message ||
          response.data?.message ||
          response.data?.detail ||
          "Email OTP verification failed"
      );
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.detail?.message ||
          err?.response?.data?.message ||
          err?.response?.data?.detail ||
          err.message ||
          "Error verifying email OTP"
      );
    }
  }
);

export const sendPhoneOtp = createAsyncThunk(
  "auth/sendPhoneOtp",
  async (data, { rejectWithValue }) => {
    const { phone_number } = data;
    try {
      const response = await postService("/send-phone-otp", { phone_number });

      if (response.status >= 200 && response.status < 300) {
        return response.data;
      }
      return rejectWithValue(
        response.data?.detail?.message ||
          response.data?.message ||
          response.data?.detail ||
          "Failed to send phone OTP"
      );
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.detail?.message ||
          err?.response?.data?.message ||
          err?.response?.data?.detail ||
          err.message ||
          "Failed to send phone OTP"
      );
    }
  }
);

export const verifyPhoneOtp = createAsyncThunk(
  "auth/verifyPhoneOtp",
  async (data, { rejectWithValue }) => {
    const { phone_number, otp } = data;
    try {
      const response = await postService("/verify-phone-otp", { phone_number, otp });

      if (response.status >= 200 && response.status < 300) {
        return response.data;
      }
      return rejectWithValue(
        response.data?.detail?.message ||
          response.data?.message ||
          response.data?.detail ||
          "Phone OTP verification failed"
      );
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.detail?.message ||
          err?.response?.data?.message ||
          err?.response?.data?.detail ||
          err.message ||
          "Error verifying phone OTP"
      );
    }
  }
);

export const signUp = createAsyncThunk(
  "auth/signUp",
  async (data, { rejectWithValue }) => {
    // const { username, password, email, role } = data;
    try {
      
      const response = await postService("/signup", data);

      if (response.status === 201 || response.status === 200) {
        const resData = response.data;
        if (resData?.token && data?.email) {
          const email = data.email;
          localStorage.setItem(SEC_AUTH_M_TK, resData.token);
          localStorage.setItem("design", resData.role);
          localStorage.setItem("_un", email);
          localStorage.setItem("convKey", `UI_${email}_${uuidv4()}`);
          triggerSubscriptionUpdate();
          triggerNavigationUpdateAfterAuth();
        }
        return resData;
      } else {
        // If the status code isn't 201, treat it as an error
        return rejectWithValue(response.data?.message || "Signup failed");
      }
    } catch (err) {
      console.error('Signup error details:', err);
      
      // Handle specific validation errors
      if (err?.response?.data?.detail && Array.isArray(err.response.data.detail)) {
        const validationErrors = err.response.data.detail;
        const termIdError = validationErrors.find(error => 
          error.loc?.includes('term_id') && error.type === 'int_type'
        );
        
        if (termIdError) {
          return rejectWithValue("Terms and Conditions are required. Please refresh the page and try again.");
        }
        
        // Handle other validation errors
        const errorMessages = validationErrors.map(error => error.msg).join(', ');
        return rejectWithValue(errorMessages);
      }
      
      // Extract backend error message if present
      const backendMessage =
        err?.response?.data?.detail?.message ||
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err.message ||
        "Something went wrong. Please try again later.";
      return rejectWithValue(backendMessage);
    }
  }
);

export const verifyOtp = createAsyncThunk(
  "auth/verifyOtp",
  async (data, { rejectWithValue }) => {
    try {
      const response = await postService("/verify-otp", data);

      if (response.status === 200) {
        // Check if token is present (for signup flow)
        if (response.data.token) {
          const { email } = data;
          const token = response.data.token;
          const role = response.data.role;

          // Set new user data for signup flow
          localStorage.setItem(SEC_AUTH_M_TK, token);
          localStorage.setItem("design", role);
          localStorage.setItem("_un", email);
          localStorage.setItem("convKey", `UI_${email}_${uuidv4()}`);

          // Trigger subscription update to ensure the subscription status is refreshed
          triggerSubscriptionUpdate();
          
          // Trigger navigation update to ensure menu reflects new subscription status
          triggerNavigationUpdateAfterAuth();
        }
        
        return response.data;
      } else {
        return rejectWithValue(
          response.data?.detail?.message ||
          response.data?.message ||
          response.data?.detail ||
          "OTP verification failed"
        );
      }
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.detail?.message ||
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err.message ||
        "Error verifying OTP"
      );
    }
  }
);

export const resendOtp = createAsyncThunk(
  "auth/resendOtp",
  async (data, { rejectWithValue }) => {
    try {
      const response = await postService("/resend-otp", data);

      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(
          response.data?.detail?.message ||
          response.data?.message ||
          response.data?.detail ||
          "Failed to resend OTP"
        );
      }
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.detail?.message ||
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err.message ||
        "Error resending OTP"
      );
    }
  }
);

export const getActiveTermsAndConditions = createAsyncThunk(
  "auth/getActiveTermsAndConditions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await API.get('/get_active_terms_and_conditions');
      return response.data;
    } catch (error) {
      console.error('Network error fetching terms and conditions:', error);
      
      const errorMessage = error?.response?.data?.detail?.message ||
                          error?.response?.data?.message ||
                          error?.response?.data?.detail ||
                          error?.message ||
                          "Network error: Unable to fetch terms and conditions";
      
      return rejectWithValue(errorMessage);
    }
  }
);

export const resetPasswordWithToken = createAsyncThunk(
  "auth/resetPasswordWithToken",
  async (data, { rejectWithValue }) => {
    try {
     

      // Check if this is a forgot password flow or admin-created user flow
      const isForgotPasswordFlow = data.isForgotPasswordFlow;

      let requestData;
      let endpoint;

      if (isForgotPasswordFlow) {
        // For forgot password flow
        requestData = {
          token: data.token,
          new_password: data.new_password
        };
        endpoint = "/forget-reset-password";
      } else {
        // For admin-created user flow
        requestData = {
          token: data.token,
          current_password: data.current_password,
          new_password: data.new_password
        };
        endpoint = "/user-reset-password";
      }

      
      const response = await postService(endpoint, requestData);

      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(
          response.data?.detail?.message ||
          response.data?.message ||
          response.data?.detail ||
          "Failed to reset password"
        );
      }
    } catch (err) {
      console.error("Password reset error:", err);

      // Extract the error message from the response
      const errorMessage = err?.response?.data?.detail?.message ||
                          err?.response?.data?.message ||
                          err?.response?.data?.detail ||
                          err.message ||
                          "Error resetting password";

      return rejectWithValue(errorMessage);
    }
  }
);


export const signOut = createAsyncThunk(
  "auth/signOut",
  async (_, { getState, rejectWithValue }) => {
    const token = getState().auth.token;
    try {
      const response = await postService(
        "/logout",
        {},
        {
          Authorization: `Bearer ${token}`,
        }
      );

      if (response.status === 200) {
        // Use the comprehensive logout utility to clear all localStorage and broadcast to other tabs
        performFullLogout();
        return response.data;
      } else {
        return rejectWithValue(
          response.data?.detail?.message ||
          response.data?.message ||
          response.data?.detail ||
          "Error logging out"
        );
      }
    } catch (err) {
      // Even if the API call fails, we should still log the user out locally
      performFullLogout();
      return rejectWithValue(
        err?.response?.data?.detail?.message ||
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err.message ||
        "Error logging out"
      );
    }
  }
);

const GOOGLE_OAUTH_ENDPOINT = "/auth/google";
const MICROSOFT_OAUTH_ENDPOINT = "/auth/microsoft";

export const googleAuth = createAsyncThunk(
  "auth/googleAuth",
  async ({ idToken, mode = "login", signupFields = {} }, thunkAPI) =>
    runGoogleAuth(GOOGLE_OAUTH_ENDPOINT, { idToken, mode, signupFields }, thunkAPI)
);

const runMicrosoftAuth = async (endpoint, { idToken, mode, signupFields }, thunkAPI) =>
  runOAuthIdTokenAuth(endpoint, idToken, "Microsoft", parseMicrosoftAuthError, thunkAPI, { mode, signupFields });

export const microsoftAuth = createAsyncThunk(
  "auth/microsoftAuth",
  async ({ idToken, mode = "login", signupFields = {} }, thunkAPI) =>
    runMicrosoftAuth(MICROSOFT_OAUTH_ENDPOINT, { idToken, mode, signupFields }, thunkAPI)
);

export const signInWithFacebook = createAsyncThunk(
  "auth/signInWithFacebook",
  async (_, { rejectWithValue }) => {
    try {
      // Import logout utilities dynamically to avoid circular dependencies
      const { clearAllLocalStorage, clearAllSessionStorage, clearReduxState } = await import("../../utils/logoutUtils");

      // Preventive cleanup: Clear any residual data from previous sessions
      clearAllLocalStorage();
      clearAllSessionStorage();
      clearReduxState();

      const response = await FirebaseService.signInFacebookRequest();
      if (response.user) {
        const token = response.user.refreshToken;
        localStorage.setItem(SEC_AUTH_M_TK, response.user.refreshToken);
        // Trigger subscription update
        triggerSubscriptionUpdate();
        // Trigger navigation update
        triggerNavigationUpdateAfterAuth();
        return token;
      } else {
        return rejectWithValue(response.message?.replace("Firebase: ", ""));
      }
    } catch (err) {
      return rejectWithValue(err.message || "Error signing in with Facebook");
    }
  }
);

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    authenticated: (state, action) => {
      state.loading = false;
      state.redirect = action.payload.redirect || "";
      state.token = action.payload.token || action.payload;
    },
    showAuthMessage: (state, action) => {
      state.message = action.payload;
      state.showMessage = true;
      state.loading = false;
    },
    hideAuthMessage: (state) => {
      state.message = "";
      state.showMessage = false;
    },
    signOutSuccess: (state) => {
      // Reset to initial state to ensure complete cleanup
      return {
        ...initialState,
        token: null,
        redirect: "",
        loading: false
      };
    },
    showLoading: (state) => {
      state.loading = true;
    },
    signInSuccess: (state, action) => {
      state.loading = false;
      state.token = action.payload;
    },
    passwordResetSuccess: (state) => {
      state.loading = false;
      state.passwordResetSuccess = true;
      state.message = "Password reset successfully!";
      state.showMessage = true;
    },
    clearPasswordResetStatus: (state) => {
      state.passwordResetSuccess = false;
    },
    clearTermsAndConditions: (state) => {
      state.termsAndConditions = null;
      state.termsLoading = false;
      state.termsError = null;
    },
    // New reducer to reset OTP state
    resetOtpState: (state) => {
      state.loginEmail = "";
      state.isOtpRequired = false;
      state.otpLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signIn.pending, (state) => {
        state.loading = true;
        state.isOtpRequired = false;
        state.loginEmail = "";
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "";
        
        if (action.payload.type === 'otp_required') {
          // OTP required flow
          state.isOtpRequired = true;
          state.loginEmail = action.payload.email;
          state.message = action.payload.message;
          state.showMessage = true;
          state.token = null;
        } else if (action.payload.type === 'direct_login') {
          // Direct login flow (legacy)
          state.token = action.payload.token;
          state.isOtpRequired = false;
          state.loginEmail = "";
        }
      })
      .addCase(signIn.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
        state.isOtpRequired = false;
        state.loginEmail = "";
      })
      .addCase(verifyOtp.pending, (state) => {
        state.loading = true;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.loading = false;
        
        // Check if token is present (for signup flow)
        if (action.payload.token) {
          state.token = action.payload.token;
          state.redirect = "";
        }
        // Don't set message here as we'll handle it in the component
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload;
        state.showMessage = true;
      })
      .addCase(verifyLoginOtp.pending, (state) => {
        state.otpLoading = true;
      })
      .addCase(verifyLoginOtp.fulfilled, (state, action) => {
        state.otpLoading = false;
        state.token = action.payload;
        state.isOtpRequired = false;
        state.loginEmail = "";
        state.message = "";
        state.showMessage = false;
      })
      .addCase(verifyLoginOtp.rejected, (state, action) => {
        state.otpLoading = false;
        state.message = action.payload;
        state.showMessage = true;
        // Keep OTP state so user can retry
      })
      .addCase(resendOtp.pending, (state) => {
        state.loading = true;
      })
      .addCase(resendOtp.fulfilled, (state, action) => {
        state.loading = false;
        // Don't set message here as we'll handle it in the component
      })
      .addCase(resendOtp.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload;
        state.showMessage = true;
      })
      .addCase(sendEmailOtp.pending, (state) => {
        state.loading = true;
      })
      .addCase(sendEmailOtp.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(sendEmailOtp.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload;
        state.showMessage = true;
      })
      .addCase(verifyEmailOtp.pending, (state) => {
        state.loading = true;
      })
      .addCase(verifyEmailOtp.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(verifyEmailOtp.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload;
        state.showMessage = true;
      })
      .addCase(sendPhoneOtp.pending, (state) => {
        state.loading = true;
      })
      .addCase(sendPhoneOtp.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(sendPhoneOtp.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload;
        state.showMessage = true;
      })
      .addCase(verifyPhoneOtp.pending, (state) => {
        state.loading = true;
      })
      .addCase(verifyPhoneOtp.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(verifyPhoneOtp.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload;
        state.showMessage = true;
      })
      .addCase(signOut.pending, (state) => {
        state.loading = true;
      })
      .addCase(signOut.fulfilled, (state) => {
        state.loading = false;
        state.token = null;
        state.redirect = "";
      })
      .addCase(signOut.rejected, (state) => {
        state.loading = false;
        state.token = null;
        state.redirect = "";
      })
      .addCase(signUp.pending, (state) => {
        state.loading = true;
      })
      .addCase(signUp.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.token) {
          state.token = action.payload.token;
          state.redirect = "";
        } else {
          state.redirect = "/login";
          state.token = null;
        }
      })
      .addCase(signUp.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(getActiveTermsAndConditions.pending, (state) => {
        state.termsLoading = true;
        state.termsError = null;
      })
      .addCase(getActiveTermsAndConditions.fulfilled, (state, action) => {
        state.termsLoading = false;
        state.termsAndConditions = action.payload;
        state.termsError = null;
      })
      .addCase(getActiveTermsAndConditions.rejected, (state, action) => {
        state.termsLoading = false;
        state.termsError = action.payload;
      })
      .addCase(googleAuth.pending, (state) => {
        state.loading = true;
      })
      .addCase(googleAuth.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "";
        state.token = action.payload.token;
        state.isNewUser = action.payload.isNewUser;
      })
      .addCase(googleAuth.rejected, (state) => {
        state.loading = false;
        state.message = '';
        state.showMessage = false;
      })
      .addCase(microsoftAuth.pending, (state) => {
        state.loading = true;
      })
      .addCase(microsoftAuth.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "";
        state.token = action.payload.token;
        state.isNewUser = action.payload.isNewUser;
      })
      .addCase(microsoftAuth.rejected, (state) => {
        state.loading = false;
        state.message = '';
        state.showMessage = false;
      })
      .addCase(signInWithFacebook.pending, (state) => {
        state.loading = true;
      })
      .addCase(signInWithFacebook.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "";
        state.token = action.payload;
      })
      .addCase(signInWithFacebook.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(resetPasswordWithToken.pending, (state) => {
        state.loading = true;
        state.passwordResetSuccess = false;
      })
      .addCase(resetPasswordWithToken.fulfilled, (state, action) => {
        state.loading = false;
        state.passwordResetSuccess = true;

        // Use the message from the API if available, otherwise use a default message based on the flow
        const isForgotFlow = action.meta.arg.isForgotPasswordFlow;
        const defaultMessage = isForgotFlow
          ? "Password reset successfully! You can now log in with your new password."
          : "Password set successfully! You can now log in with your new password.";

        state.message = action.payload.message || defaultMessage;
        state.showMessage = true;
        // Remove conflicting redirect - let component handle navigation
      })
      .addCase(resetPasswordWithToken.rejected, (state, action) => {
        state.loading = false;
        state.passwordResetSuccess = false;
        state.message = action.payload;
        state.showMessage = true;
      });
  },
});

export const {
  authenticated,
  showAuthMessage,
  hideAuthMessage,
  resetOtpState,
  signOutSuccess,
  showLoading,
  signInSuccess,
  passwordResetSuccess,
  clearPasswordResetStatus,
  clearTermsAndConditions,
} = authSlice.actions;

export default authSlice.reducer;
